import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { Send } from "lucide-react";
import { Button } from "../primitives";
import { MentionPopover, type MentionPopoverHandle } from "./MentionPopover";
import {
  useChatContext,
  type EntityContext,
} from "@/stores/chat-context-store";

interface GlobalChatInputProps {
  placeholder?: string;
  onSend: (message: string) => void;
}

export function GlobalChatInput({
  placeholder = "Ask about your documents...",
  onSend,
}: GlobalChatInputProps) {
  const [value, setValue] = useState("");
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const mentionRef = useRef<MentionPopoverHandle>(null);
  const addEntity = useChatContext((s) => s.addEntity);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const trimmed = value.trim();

  function handleSend() {
    if (!trimmed || mentionOpen) return;
    onSend(trimmed);
    setValue("");
  }

  function handleChange(newValue: string) {
    setValue(newValue);

    const cursorPos = inputRef.current?.selectionStart ?? newValue.length;
    const textBeforeCursor = newValue.slice(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf("@");

    if (lastAtIndex >= 0) {
      const charBefore = lastAtIndex > 0 ? newValue[lastAtIndex - 1] : " ";
      if (charBefore === " " || lastAtIndex === 0) {
        const query = textBeforeCursor.slice(lastAtIndex + 1);
        if (!query.includes(" ")) {
          setMentionOpen(true);
          setMentionQuery(query);
          setMentionStartIndex(lastAtIndex);
          return;
        }
      }
    }

    if (mentionOpen) {
      setMentionOpen(false);
      setMentionQuery("");
      setMentionStartIndex(-1);
    }
  }

  const handleMentionSelect = useCallback(
    (entity: EntityContext) => {
      addEntity(entity);

      // Remove the @query text from input
      if (mentionStartIndex >= 0) {
        const before = value.slice(0, mentionStartIndex);
        const cursorPos = inputRef.current?.selectionStart ?? value.length;
        const after = value.slice(cursorPos);
        setValue(before + after);
      }

      setMentionOpen(false);
      setMentionQuery("");
      setMentionStartIndex(-1);
      inputRef.current?.focus();
    },
    [addEntity, mentionStartIndex, value],
  );

  const handleMentionClose = useCallback(() => {
    setMentionOpen(false);
    setMentionQuery("");
    setMentionStartIndex(-1);
    inputRef.current?.focus();
  }, []);

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    // When mention popover is open, delegate navigation keys
    if (mentionOpen && mentionRef.current) {
      const handled = mentionRef.current.handleKey(e.key);
      if (handled) {
        e.preventDefault();
        return;
      }
    }

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  return (
    <div className="px-4 pt-2 pb-px outline-none focus:outline-none ring-0 focus:ring-0 relative top-2 py-0 border-t border-border space-y-2">
      <div className="relative">
        <MentionPopover
          ref={mentionRef}
          open={mentionOpen}
          onSelect={handleMentionSelect}
          onClose={handleMentionClose}
          searchQuery={mentionQuery}
        />

        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="flex-1 bg-card rounded-lg px-4 py-2.5 text-sm text-primary placeholder:text-muted outline-none focus:ring-1 focus:ring-transparent focus:ring-offset-0 focus:ring-offset-background"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={mentionOpen}
          />
          <Button
            variant="primary"
            icon={Send}
            iconOnly
            onClick={handleSend}
            disabled={!trimmed}
            className="h-9 w-9 rounded-lg shrink-0"
            aria-label="Send message"
          />
        </div>
      </div>
    </div>
  );
}
